import React, { useRef, useState } from "react";
import {
  Box,
  CircularProgress,
  Link,
  Chip as MuiChip,
  IconButton,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import styled from "@emotion/styled";
import PopoverTarga from "../../components/PopoverTarga";
import PopoverUser from "../../components/PopoverUser";
import useAuth from "../../../hooks/useAuth";
import { useNavigate } from "react-router-dom";
import {
  Person,
  AccountBalance,
  ReportProblem,
  WarningAmber,
} from "@mui/icons-material";
import GridOnIcon from "@mui/icons-material/GridOn";
import GavelIcon from "@mui/icons-material/Gavel";
import ShuffleIcon from "@mui/icons-material/Shuffle";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { useSelector } from "react-redux";
import PopoverReboot from "../../components/PopoverReboot";
import { useTheme } from "@mui/system";

const Chip = styled(MuiChip)`
  height: 20px;
  padding: 4px 0;
  font-size: 90%;
  background-color: ${(props) =>
    props.theme.palette[props.color ? props.color : "primary"]};
  color: ${(props) => props.theme.palette.common.white};
`;

// dato mancante (es. CVT creata senza targa o cliente): testo attenuato al
// posto della cella vuota, così si capisce che il valore non è stato inserito
const emptyValueSx = {
  color: "text.disabled",
  fontStyle: "italic",
  fontWeight: 500,
};

export const useGetRcTableColumns = (
  tableType,
  refreshData,
  onClickCliente,
  onClickTarga,
  onClickAgente,
  { showReboot = true } = {},
) => {
  const { getData } = useAuth();
  const theme = useTheme();
  const isSmallDevice = useMediaQuery("(max-width:1000px)");
  const [userData, setUserData] = useState(null);
  const [targaData, setTargaData] = useState(null);
  const [canViewUserData, setCanViewUserData] = useState(false);
  const [canViewTargaData, setCanViewTargaData] = useState(false);
  const [isTargaPopupOpen, setIsTargaPopupOpen] = useState(false);
  const [isRebootPopupOpen, setIsRebootPopupOpen] = useState(false);
  const [isUserPopupOpen, setIsUserPopupOpen] = useState(false);
  const [currentTargaRef, setCurrentTargaRef] = useState(null);
  const [currentUserRef, setCurrentUserRef] = useState(null);
  const [currentRebootRef, setCurrentRebootRef] = useState(null);
  const role = useSelector((state) => state.role.role);
  const isAdm = ["supAdm", "adm"].includes(role);
  const rebootRefs = useRef({});
  const targaRefs = useRef({});
  const userRefs = useRef({});
  const navigate = useNavigate();
  const handleNavigation = (path) => {
    // Set fromDetails state if navigating to rcAutoDetails
    if (path.includes("rcAutoDetails")) {
      // Set state on current entry for browser back button support
      const currentState = window.history.state;
      window.history.replaceState(
        { ...currentState, usr: { ...currentState?.usr, fromDetails: true } },
        "",
      );
    }
    navigate(path, { state: { fromPath: window.location.pathname } });
    window.scrollTo(0, 0);
  };

  const handleCloseTargaPopup = () => {
    setIsTargaPopupOpen(false);
    setCurrentTargaRef(null);
  };

  const handleCloseRebootPopup = () => {
    setIsRebootPopupOpen(false);
    setCurrentRebootRef(null);
  };

  const handleCloseUserPopup = () => {
    setIsUserPopupOpen(false);
    setCurrentUserRef(null);
  };

  const handleOpenTargaPopup = (ref, targaId) => {
    getData({
      action: "getPreviewInfo",
      type: "targa",
      value: targaId,
    })
      .then((response) => {
        setTargaData(response?.data);
        setCanViewTargaData(Boolean(response?.status === 1));
        setIsTargaPopupOpen(true);
        setCurrentTargaRef(ref);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const handleOpenUserPopup = (ref, utenteIDUID) => {
    getData({
      action: "getPreviewInfo",
      type: "utente",
      value: utenteIDUID,
    })
      .then((response) => {
        setUserData(response?.data);
        setCanViewUserData(Boolean(response?.status === 1));
        setIsUserPopupOpen(true);
        setCurrentUserRef(ref);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const column = [
    {
      field: "Targa",
      headerName: "Targa",
      sortable: false,
      filterable: false,
      minWidth: 200,
      width: 200,
      flex: 1,
      renderCell: (params) => {
        const {
          targa,
          tipoVeicolo,
          idUID,
          rebootTarga,
          idSceltaPreventivo,
          sceltaPreventivoDesc,
        } = params.row;
        const ref = (el) => {
          targaRefs.current[params.id] = el;
        };
        const ref2 = (el) => {
          rebootRefs.current[params.id] = el;
        };
        const canReboot = showReboot && rebootTarga && rebootTarga > 0;
        const hasTarga = Boolean(String(targa ?? "").trim());

        return (
          <Box
            sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}
          >
            {showReboot && (
              <PopoverReboot
                idUID={idUID}
                targa={targa}
                isRebootPopoverOpen={
                  isRebootPopupOpen &&
                  currentRebootRef === rebootRefs.current[params.id]
                }
                handleCloseRebootPopup={handleCloseRebootPopup}
                rebootRef={rebootRefs.current[params.id]}
                refreshData={refreshData}
              />
            )}
            {canReboot && (
              <IconButton
                ref={ref2}
                onClick={() => {
                  setIsRebootPopupOpen(true); // Open the popover
                  setCurrentRebootRef(rebootRefs.current[params.id]); // Set the correct reference
                }}
              >
                <RestartAltIcon />
              </IconButton>
            )}
            {hasTarga ? (
              <IconButton
                ref={ref}
                onClick={() => {
                  if (onClickTarga) {
                    onClickTarga(params.row);
                  } else {
                    handleOpenTargaPopup(targaRefs.current[params.id], idUID);
                  }
                }}
                size="small"
                sx={{
                  margin: "0 4px",
                }}
              >
                <InfoIcon fontSize="small" />
              </IconButton>
            ) : (
              // stesso ingombro del pulsante info, per tenere allineate le targhe
              <Box sx={{ width: 30, margin: "0 4px", flexShrink: 0 }} />
            )}
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <PopoverTarga
                data={targaData}
                idUID={idUID}
                canViewTargaData={canViewTargaData}
                isTargaPopupOpen={
                  isTargaPopupOpen &&
                  currentTargaRef === targaRefs.current[params.id]
                }
                handleCloseTargaPopup={handleCloseTargaPopup}
                targaRef={targaRefs.current[params.id]}
                params={params.row}
              />
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                }}
              >
                <Link
                  underline={hasTarga ? undefined : "hover"}
                  sx={{
                    cursor: "pointer",
                    minWidth: "62px",
                    ...(hasTarga
                      ? {
                          color:
                            theme.palette.mode === "light"
                              ? "#0c4bb7"
                              : "#5c92e1",
                          fontWeight: 900,
                        }
                      : emptyValueSx),
                  }}
                  onClick={() =>
                    handleNavigation(`/pages/rcAutoDetails/${idUID}`)
                  }
                >
                  {hasTarga ? targa : "Targa non indicata"}
                </Link>
              </Box>

              <Box sx={{ fontSize: "smaller", fontWeight: 900 }}>
                {tipoVeicolo}
              </Box>
            </Box>
          </Box>
        );
      },
    },
    {
      field: "Cliente",
      headerName: "Cliente",
      sortable: false,
      filterable: false,
      minWidth: 220,
      width: 220,
      flex: 1,
      renderCell: (params) => {
        const {
          clienteRagSociale,
          clienteCognome,
          clienteNome,
          comuneResidenza,
          clienteIDUID,
        } = params.row;
        const nominativo = [clienteCognome, clienteNome]
          .filter(Boolean)
          .join(" ");
        return (
          <Box
            sx={{
              cursor: onClickCliente ? "pointer" : "default",
              width: "100%",
            }}
            onClick={() => onClickCliente && onClickCliente(params.row)}
          >
            <Box>
              {clienteRagSociale ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                  }}
                >
                  <AccountBalance />
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      marginLeft: "8px",
                    }}
                  >
                    <Box
                      sx={{
                        color:
                          theme.palette.mode === "light"
                            ? "#0c4bb7"
                            : "#5c92e1",
                        fontWeight: 900,
                      }}
                    >
                      {clienteRagSociale}
                    </Box>
                    <Box sx={{ fontSize: "smaller", fontWeight: 900 }}>
                      {params.row.comuneSedeLegale}
                    </Box>
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                  }}
                >
                  <Person
                    sx={nominativo ? undefined : { color: "text.disabled" }}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      marginLeft: "8px",
                    }}
                  >
                    <Box
                      sx={
                        nominativo
                          ? {
                              color:
                                theme.palette.mode === "light"
                                  ? "#0c4bb7"
                                  : "#5c92e1",
                              fontWeight: 900,
                            }
                          : emptyValueSx
                      }
                    >
                      {nominativo || "Cliente non indicato"}
                    </Box>
                    <Box
                      sx={{
                        fontSize: "smaller",
                        fontWeight: 900,
                      }}
                    >
                      {comuneResidenza}
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        );
      },
    },
    {
      field: "EseguitaDa",
      headerName: "Eseguita da",
      sortable: false,
      filterable: false,
      minWidth: 250,
      width: 300,
      flex: 1,
      align: "left",
      renderCell: (params) => {
        const { utenteIDUID, utenteCognome, utenteNome, utenteAzienda } =
          params.row;
        const ref = (el) => {
          userRefs.current[params.id] = el;
        };

        return (
          <Box
            sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}
          >
            <IconButton
              ref={ref}
              onClick={() => {
                if (onClickAgente) {
                  onClickAgente(params.row);
                } else {
                  handleOpenUserPopup(userRefs.current[params.id], utenteIDUID);
                }
              }}
              size="small"
              sx={{
                margin: "0 8px",
              }}
            >
              <InfoIcon fontSize="small" />
            </IconButton>
            <Box>
              <PopoverUser
                data={userData}
                canViewUserData={canViewUserData}
                utenteIDUID={utenteIDUID}
                isUserPopupOpen={
                  isUserPopupOpen &&
                  currentUserRef === userRefs.current[params.id]
                }
                handleCloseUserPopup={handleCloseUserPopup}
                userRef={userRefs.current[params.id]}
                params={params.row}
              />
              <Link
                sx={{
                  cursor: "pointer",
                  color: theme.palette.mode === "light" ? "#0c4bb7" : "#5c92e1",
                  fontWeight: 900,
                }}
                onClick={() =>
                  handleNavigation(`/pages/profile/${utenteIDUID}`)
                }
              >{`${utenteCognome} ${utenteNome}`}</Link>

              <Box sx={{ fontSize: "smaller", fontWeight: 900 }}>
                {utenteAzienda}
              </Box>
            </Box>
          </Box>
        );
      },
    },
    {
      field: "Visura",
      headerName: tableType === "cvt" ? "Ricerca" : "Visura",
      sortable: false,
      filterable: false,
      minWidth: 200,
      width: 200,
      flex: 1,
      renderCell: (params) => {
        const {
          idStatoRicerca,
          statoDesc,
          note,
          dataCreazione,
          idSceltaPreventivo,
          sceltaPreventivoDesc,
        } = params.row;

        const RenderIcon = () => {
          const iconStyle = {
            width: "16px",
            height: "16px",
            marginLeft: isSmallDevice ? "0" : "6px",
            marginRight: isSmallDevice ? "0" : "-4px",
          };
          switch (idSceltaPreventivo) {
            case 1:
              return <GridOnIcon sx={iconStyle} />;
            case 2:
              return <GavelIcon sx={iconStyle} />;
            case 3:
              return <ShuffleIcon sx={iconStyle} />;
            default:
              return null;
          }
        };

        const completed = idStatoRicerca === 1;
        const loading =
          idStatoRicerca === 2 || idStatoRicerca === 6 || idStatoRicerca === 8;
        const action = idStatoRicerca === 9;

        const ChipItem = ({ color, showLoading, clickable, customColor }) => (
          <Chip
            label={
              <Box sx={{ display: "flex", flexDirection: "row" }}>
                {statoDesc}
                {showLoading && (
                  <CircularProgress
                    size={14}
                    sx={{ marginLeft: "8px", color: "white", marginTop: "1px" }}
                  />
                )}
              </Box>
            }
            color={color}
            sx={{
              ...(customColor && { backgroundColor: customColor }),
              color: "white",
              ...(clickable ? { cursor: "pointer" } : {}),
              userSelect: "none",
            }}
          />
        );

        const getChip = () => {
          if (completed)
            return <ChipItem color="success" customColor="#2e7d32" />;
          if (loading)
            return (
              <ChipItem color="warning" showLoading customColor="#ed6c02" />
            );
          if (action)
            return <ChipItem color="info" clickable customColor="#0f74b9" />;
          return <ChipItem color="error" customColor="#d32f2f" />;
        };

        const getCellContent = () => {
          return (
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                }}
              >
                {getChip()}
                {idSceltaPreventivo > 0 && (
                  <Chip
                    sx={{
                      marginLeft: "6px",
                      backgroundColor: "#ed6c02",
                    }}
                    color="warning"
                    label={
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                        }}
                      >
                        {!isSmallDevice && sceltaPreventivoDesc}
                        <RenderIcon />
                      </Box>
                    }
                  />
                )}
              </Box>
              <Box
                sx={{
                  fontSize: "smaller",
                  marginTop: "2px",
                  fontWeight: 900,
                }}
              >
                {dataCreazione}
              </Box>
            </Box>
          );
        };

        return note ? (
          <Box display="flex" gap={1} alignItems="center">
            <Tooltip
              title={note.split("|").map((note, index) => (
                <div key={index}>{note}</div>
              ))}
              enterTouchDelay={0}
            >
              <WarningAmber fontSize="medium" />
            </Tooltip>
            {getCellContent()}
          </Box>
        ) : (
          getCellContent()
        );
      },
    },
    {
      field: "Preventivi",
      headerName: "Preventivi",
      sortable: false,
      filterable: false,
      minWidth: 240,
      width: 240,
      flex: 1,
      renderCell: (params) => {
        const {
          idStatoPreventivi,
          notePreventivi,
          preventiviInLavorazione,
          preventiviTotale,
          statoPreventiviDesc,
        } = params.row;

        const completed = idStatoPreventivi === 1;
        const loading =
          idStatoPreventivi === 2 ||
          idStatoPreventivi === 6 ||
          idStatoPreventivi === 8;

        const ProgressIndicator = () => (
          <Box sx={{ marginLeft: "8px" }}>
            {`${preventiviInLavorazione} / ${preventiviTotale}`}
          </Box>
        );

        const ChipItem = ({
          color,
          showProgress,
          showLoading,
          customColor,
        }) => (
          <Chip
            label={
              <Box sx={{ display: "flex", flexDirection: "row" }}>
                {statoPreventiviDesc}
                {showLoading && (
                  <CircularProgress
                    size={14}
                    sx={{ marginLeft: "8px", color: "white", marginTop: "1px" }}
                  />
                )}
                {showProgress && preventiviTotale > 0 && <ProgressIndicator />}
              </Box>
            }
            color={color}
            sx={{
              ...(customColor && { backgroundColor: customColor }),
              color: "white",
              userSelect: "none",
            }}
          />
        );

        const getChip = () => {
          if (completed)
            return (
              <ChipItem color="success" showProgress customColor="#2e7d32" />
            );
          if (loading)
            return (
              <ChipItem
                color="warning"
                showProgress
                showLoading
                customColor="#ed6c02"
              />
            );
          return <ChipItem color="error" showProgress customColor="#d32f2f" />;
        };

        return (
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "start",
                justifyContent: "start",
              }}
            >
              {getChip()}
              {notePreventivi && (
                <Box
                  sx={{
                    fontSize: "smaller",
                    marginTop: "2px",
                    fontWeight: 900,
                  }}
                >
                  {notePreventivi.split("|").map((note, index) => (
                    <div key={index}>{note}</div>
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        );
      },
    },
  ];

  return column;
};
